import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Users, Building, Globe, Calendar, UserPlus, Gift, Loader2, Mail, Phone, MapPin, Clock, Briefcase, Video, Shield, UserCheck } from 'lucide-react';
import { employeeDirectoryService } from '../../services/employee-directory';
import { EmployeeDashboardStats, Employee } from '../../types/employee-directory';
import EmployeeGlobe from '../../components/employee-directory/EmployeeGlobe';
import { useAuth } from '../../context/AuthContext';

interface WorkAnniversary {
  firstName: string;
  lastName: string;
  anniversaryDate: string;
  yearsOfService: number;
}

interface Birthday {
  firstName: string;
  lastName: string;
  upcomingBirthday: string;
}

const EmployeeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<EmployeeDashboardStats | null>(null);
  const [anniversaries, setAnniversaries] = useState<WorkAnniversary[]>([]);
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const [loading, setLoading] = useState(true);
  const [anniversariesLoading, setAnniversariesLoading] = useState(true);
  const [birthdaysLoading, setBirthdaysLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
  const [showRecentHiresModal, setShowRecentHiresModal] = useState(false);
  const [showAllAnniversariesModal, setShowAllAnniversariesModal] = useState(false);
  const [showAllBirthdaysModal, setShowAllBirthdaysModal] = useState(false);
  const [showCountryEmployeesModal, setShowCountryEmployeesModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  
  // Modal data states
  const [departmentBreakdown, setDepartmentBreakdown] = useState<Array<{department: string, count: number}>>([]);
  const [recentHires, setRecentHires] = useState<Employee[]>([]);
  const [allAnniversaries, setAllAnniversaries] = useState<(WorkAnniversary & { employee: Employee })[]>([]);
  const [allBirthdays, setAllBirthdays] = useState<(Birthday & { employee: Employee })[]>([]);
  const [countryEmployees, setCountryEmployees] = useState<Employee[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [showGlobeModal, setShowGlobeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setAnniversariesLoading(true);
        setBirthdaysLoading(true);
        setError(null);
        
        // Single API call replaces 3+ separate calls
        const completeData = await employeeDirectoryService.getDashboardComplete();
        
        // Set dashboard stats (transformed from unified data)
        setStats({
          totalEmployees: completeData.stats.totalEmployees,
          totalDepartments: completeData.stats.totalDepartments,
          totalCountries: completeData.stats.totalCountries,
          totalCities: completeData.stats.totalCities,
          activeEmployees: completeData.stats.activeEmployees,
          newHiresThisMonth: completeData.stats.newHiresThisMonth,
          upcomingBirthdays: completeData.upcomingBirthdays || [],
          upcomingAnniversaries: completeData.upcomingAnniversaries || [],
          departmentBreakdown: completeData.departmentBreakdown || [],
          countryBreakdown: completeData.countryBreakdown || [],
          lastUpdated: completeData.stats.lastUpdated
        });
        
        // Set pre-calculated anniversaries and birthdays (no client-side processing needed)
        setAnniversaries(completeData.upcomingAnniversaries?.slice(0, 5) || []);
        setBirthdays(completeData.upcomingBirthdays?.slice(0, 5) || []);
        
        // Store complete data for modal operations (eliminates additional API calls)
        (window as any).employeeDirectoryCompleteData = completeData;
        
      } catch (err) {
        console.error('Employee Directory Dashboard error:', err);
        setError('Failed to load dashboard data');
        
        // Fallback to original method if unified endpoint fails
        try {
          const dashboardStats = await employeeDirectoryService.getDashboardMetrics();
          setStats(dashboardStats);
          await loadUpcomingAnniversaries();
          await loadUpcomingBirthdays();
        } catch (fallbackErr) {
          console.error('Fallback method also failed:', fallbackErr);
        }
      } finally {
        setLoading(false);
        setAnniversariesLoading(false);
        setBirthdaysLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const loadUpcomingAnniversaries = async () => {
    try {
      setAnniversariesLoading(true);
      const employees = await employeeDirectoryService.getAllEmployees();
      
      // Calculate upcoming anniversaries (next 30 days)
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      
      const upcomingAnniversaries: WorkAnniversary[] = [];
      
      employees.forEach(employee => {
        if (employee.startDate) {
          const startDate = new Date(employee.startDate);
          const currentYear = today.getFullYear();
          
          // Create anniversary date for this year
          const anniversaryThisYear = new Date(currentYear, startDate.getMonth(), startDate.getDate());
          
          // If anniversary already passed this year, check next year
          if (anniversaryThisYear < today) {
            anniversaryThisYear.setFullYear(currentYear + 1);
          }
          
          // Check if anniversary is within next 30 days
          if (anniversaryThisYear <= thirtyDaysFromNow) {
            const yearsOfService = currentYear - startDate.getFullYear();
            if (anniversaryThisYear.getFullYear() > currentYear) {
              // Next year anniversary
              upcomingAnniversaries.push({
                firstName: employee.firstName,
                lastName: employee.lastName,
                anniversaryDate: anniversaryThisYear.toISOString(),
                yearsOfService: yearsOfService + 1
              });
            } else {
              // This year anniversary
              upcomingAnniversaries.push({
                firstName: employee.firstName,
                lastName: employee.lastName,
                anniversaryDate: anniversaryThisYear.toISOString(),
                yearsOfService: yearsOfService
              });
            }
          }
        }
      });
      
      // Sort by date (ascending - earliest first)
      upcomingAnniversaries.sort((a, b) => new Date(a.anniversaryDate).getTime() - new Date(b.anniversaryDate).getTime());
      setAnniversaries(upcomingAnniversaries);
    } catch (error) {
      console.error('Error loading upcoming anniversaries:', error);
    } finally {
      setAnniversariesLoading(false);
    }
  };

  const loadUpcomingBirthdays = async () => {
    try {
      setBirthdaysLoading(true);
      const employees = await employeeDirectoryService.getAllEmployees();
      
      // Calculate upcoming birthdays (next 30 days)
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      
      const upcomingBirthdays: Birthday[] = [];
      
      employees.forEach(employee => {
        if (employee.birthday) {
          const birthday = new Date(employee.birthday);
          const currentYear = today.getFullYear();
          
          // Create birthday date for this year
          const birthdayThisYear = new Date(currentYear, birthday.getMonth(), birthday.getDate());
          
          // If birthday already passed this year, check next year
          if (birthdayThisYear < today) {
            birthdayThisYear.setFullYear(currentYear + 1);
          }
          
          // Check if birthday is within next 30 days
          if (birthdayThisYear <= thirtyDaysFromNow) {
            upcomingBirthdays.push({
              firstName: employee.firstName,
              lastName: employee.lastName,
              upcomingBirthday: birthdayThisYear.toISOString()
            });
          }
        }
      });
      
      // Sort by date (ascending - earliest first)
      upcomingBirthdays.sort((a, b) => new Date(a.upcomingBirthday).getTime() - new Date(b.upcomingBirthday).getTime());
      setBirthdays(upcomingBirthdays);
    } catch (error) {
      console.error('Error loading upcoming birthdays:', error);
    } finally {
      setBirthdaysLoading(false);
    }
  };

  // Click handlers
  const handleTotalEmployeesClick = () => {
    navigate('/employee-directory/directory');
  };

  const handleTotalCountriesClick = () => {
    setShowGlobeModal(true);
  };

  const handleTotalDepartmentsClick = async () => {
    setShowDepartmentsModal(true);
    setModalLoading(true);
    try {
      const completeData = (window as any).employeeDirectoryCompleteData;
      
      if (completeData && completeData.departmentBreakdown) {
        setDepartmentBreakdown(completeData.departmentBreakdown);
      } else {
        const employees = await employeeDirectoryService.getAllEmployees();
        const deptMap = new Map<string, number>();
        
        employees.forEach(employee => {
          if (employee.department) {
            deptMap.set(employee.department, (deptMap.get(employee.department) || 0) + 1);
          }
        });
        
        const breakdown = Array.from(deptMap.entries())
          .map(([department, count]) => ({ department, count }))
          .sort((a, b) => b.count - a.count);
        
        setDepartmentBreakdown(breakdown);
      }
    } catch (error) {
      console.error('Error loading department breakdown:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleRecentHiresClick = async () => {
    setShowRecentHiresModal(true);
    setModalLoading(true);
    try {
      const completeData = (window as any).employeeDirectoryCompleteData;
      
      if (completeData && completeData.employees) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recent = completeData.employees.filter((employee: any) => {
          if (!employee.startDate) return false;
          const startDate = new Date(employee.startDate);
          return startDate >= thirtyDaysAgo;
        }).sort((a: any, b: any) => new Date(b.startDate!).getTime() - new Date(a.startDate!).getTime());
        
        setRecentHires(recent);
      } else {
        const employees = await employeeDirectoryService.getAllEmployees();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recent = employees.filter(employee => {
          if (!employee.startDate) return false;
          const startDate = new Date(employee.startDate);
          return startDate >= thirtyDaysAgo;
        }).sort((a, b) => new Date(b.startDate!).getTime() - new Date(a.startDate!).getTime());
        
        setRecentHires(recent);
      }
    } catch (error) {
      console.error('Error loading recent hires:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleShowAllAnniversaries = async () => {
    setShowAllAnniversariesModal(true);
    setModalLoading(true);
    
    try {
      const completeData = (window as any).employeeDirectoryCompleteData;
      
      if (completeData && completeData.allAnniversaries) {
        setAllAnniversaries(completeData.allAnniversaries);
      } else {
        const employees = await employeeDirectoryService.getAllEmployees();
        const today = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        
        const upcomingAnniversariesList: (WorkAnniversary & { employee: Employee })[] = [];
        
        employees.forEach(employee => {
          if (employee.startDate) {
            const startDate = new Date(employee.startDate);
            const currentYear = today.getFullYear();
            
            // Create anniversary date for this year
            const anniversaryThisYear = new Date(currentYear, startDate.getMonth(), startDate.getDate());
            
            // If anniversary already passed this year, check next year
            if (anniversaryThisYear < today) {
              anniversaryThisYear.setFullYear(currentYear + 1);
            }
            
            // Only include anniversaries within next 30 days
            if (anniversaryThisYear <= thirtyDaysFromNow) {
              const yearsOfService = currentYear - startDate.getFullYear();
              if (anniversaryThisYear.getFullYear() > currentYear) {
                upcomingAnniversariesList.push({
                  firstName: employee.firstName,
                  lastName: employee.lastName,
                  anniversaryDate: anniversaryThisYear.toISOString(),
                  yearsOfService: yearsOfService + 1,
                  employee: employee
                });
              } else {
                upcomingAnniversariesList.push({
                  firstName: employee.firstName,
                  lastName: employee.lastName,
                  anniversaryDate: anniversaryThisYear.toISOString(),
                  yearsOfService: yearsOfService,
                  employee: employee
                });
              }
            }
          }
        });
        
        // Sort by date (ascending - earliest first)
        upcomingAnniversariesList.sort((a, b) => new Date(a.anniversaryDate).getTime() - new Date(b.anniversaryDate).getTime());
        setAllAnniversaries(upcomingAnniversariesList);
      }
    } catch (error) {
      console.error('Error loading all anniversaries:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleShowAllBirthdays = async () => {
    setShowAllBirthdaysModal(true);
    setModalLoading(true);
    
    try {
      const completeData = (window as any).employeeDirectoryCompleteData;
      
      if (completeData && completeData.allBirthdays) {
        setAllBirthdays(completeData.allBirthdays);
      } else {
        const employees = await employeeDirectoryService.getAllEmployees();
        const today = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        const currentYear = today.getFullYear();
        
        const upcomingBirthdaysList: (Birthday & { employee: Employee })[] = [];
        
        employees.forEach(employee => {
          if (employee.birthday) {
            const birthday = new Date(employee.birthday);
            
            // Create birthday date for this year
            const birthdayThisYear = new Date(currentYear, birthday.getMonth(), birthday.getDate());
            
            // If birthday already passed this year, check next year
            if (birthdayThisYear < today) {
              birthdayThisYear.setFullYear(currentYear + 1);
            }
            
            // Only include birthdays within next 30 days
            if (birthdayThisYear <= thirtyDaysFromNow) {
              upcomingBirthdaysList.push({
                firstName: employee.firstName,
                lastName: employee.lastName,
                upcomingBirthday: birthdayThisYear.toISOString(),
                employee: employee
              });
            }
          }
        });
        
        // Sort by date (ascending - earliest first)
        upcomingBirthdaysList.sort((a, b) => new Date(a.upcomingBirthday).getTime() - new Date(b.upcomingBirthday).getTime());
        setAllBirthdays(upcomingBirthdaysList);
      }
    } catch (error) {
      console.error('Error loading all birthdays:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCountryClick = async (country: string) => {
    setSelectedCountry(country);
    setShowCountryEmployeesModal(true);
    setModalLoading(true);
    try {
      const completeData = (window as any).employeeDirectoryCompleteData;
      
      if (completeData && completeData.employees) {
        const employeesFromCountry = completeData.employees.filter((employee: any) => employee.country === country);
        setCountryEmployees(employeesFromCountry);
      } else {
        const employees = await employeeDirectoryService.getAllEmployees();
        const employeesFromCountry = employees.filter(employee => employee.country === country);
        setCountryEmployees(employeesFromCountry);
      }
    } catch (error) {
      console.error('Error loading country employees:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Email click handler for Gmail integration
  const handleEmailClick = (email: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
      window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open Gmail:', error);
    }
  };

  // Country code mapping
  const getCountryFlag = (country: string): string => {
    const flagMap: Record<string, string> = {
      'United States of America': 'US',
      'Philippines': 'PH',
      'Ghana': 'GH',
      'Argentina': 'AR',
      'Mexico': 'MX',
      'Czech Republic': 'CZ',
      'India': 'IN',
      'Spain': 'ES',
      'Indonesia': 'ID',
      'Pakistan': 'PK',
      'France': 'FR',
      'Nicaragua': 'NI',
      'Romania': 'RO',
      'Cambodia': 'KH',
      'Serbia': 'RS',
      'Germany': 'DE',
      'Ecuador': 'EC',
      'Brazil': 'BR',
      'Hungary': 'HU',
      'Bangladesh': 'BD',
      'Egypt': 'EG',
      'United Kingdom': 'UK'
    };
    return flagMap[country] || '--';
  };

  // Format date for display (e.g., "16th July", "2nd April")
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month}`;
  };

  // Sort countries by employee count (descending)
  const sortedCountries = stats?.countryBreakdown ? 
    stats.countryBreakdown
      .sort((a, b) => b.count - a.count)
      .map(item => [item.country, item.count] as [string, number])
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dashboard Header Row */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#2563EB] dark:text-[#3B82F6]">Dashboard</h1>
        <div className="text-sm text-muted-foreground">
          Welcome back, {user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'User'}!
        </div>
      </div>

      {/* First Row - 4 Statistics Widgets */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
        {/* Total Employees Widget */}
        <Card
          className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
          onClick={handleTotalEmployeesClick}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs md:text-sm font-medium">Total Employees</CardTitle>
            <Users className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pb-2">
            <div className="text-lg md:text-2xl font-bold text-[#2563EB] dark:text-[#3B82F6]">
              {loading ? (
                <div className="animate-pulse bg-muted h-6 md:h-8 w-12 md:w-16 rounded"></div>
              ) : (
                stats?.totalEmployees || 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Active employees
            </p>
          </CardContent>
        </Card>

        {/* Total Countries Widget */}
        <Card
          className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
          onClick={handleTotalCountriesClick}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs md:text-sm font-medium">Total Countries</CardTitle>
            <Globe className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pb-2">
            <div className="text-lg md:text-2xl font-bold text-[#2563EB] dark:text-[#3B82F6]">
              {loading ? (
                <div className="animate-pulse bg-muted h-6 md:h-8 w-12 md:w-16 rounded"></div>
              ) : (
                stats?.totalCountries || 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Countries with employees
            </p>
          </CardContent>
        </Card>

        {/* Total Departments Widget */}
        <Card
          className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
          onClick={handleTotalDepartmentsClick}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs md:text-sm font-medium">Total Departments</CardTitle>
            <Building className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pb-2">
            <div className="text-lg md:text-2xl font-bold text-[#2563EB] dark:text-[#3B82F6]">
              {loading ? (
                <div className="animate-pulse bg-muted h-6 md:h-8 w-12 md:w-16 rounded"></div>
              ) : (
                stats?.totalDepartments || 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Active departments
            </p>
          </CardContent>
        </Card>

        {/* Recent Hires Widget */}
        <Card
          className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
          onClick={handleRecentHiresClick}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs md:text-sm font-medium">Recent Hires</CardTitle>
            <UserPlus className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pb-2">
            <div className="text-lg md:text-2xl font-bold text-[#2563EB] dark:text-[#3B82F6]">
              {loading ? (
                <div className="animate-pulse bg-muted h-6 md:h-8 w-12 md:w-16 rounded"></div>
              ) : (
                stats?.newHiresThisMonth || 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Second Row - Three Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Employees by Country */}
        <Card
          className="bg-gray-100 dark:bg-gray-800/50"
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Employees by Country
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex justify-between items-center py-1.5">
                    <div className="animate-pulse bg-muted h-4 w-32 rounded"></div>
                    <div className="animate-pulse bg-muted h-4 w-8 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {sortedCountries.length > 0 ? (
                  sortedCountries.map(([country, count]) => (
                    <div
                      key={country}
                      className="flex justify-between items-center py-1.5 px-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer"
                      onClick={() => handleCountryClick(country)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{getCountryFlag(country)}</span>
                        <span className="font-medium text-sm">{country}</span>
                      </div>
                      <Badge variant="secondary" className="font-semibold text-xs">
                        {count}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted-foreground py-4">
                    No country data available
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Work Anniversaries */}
        <Card
          className="bg-gray-100 dark:bg-gray-800/50"
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 cursor-pointer hover:text-primary transition-colors" onClick={handleShowAllAnniversaries}>
              <Calendar className="h-5 w-5" />
              Work Anniversaries
            </CardTitle>
            <p className="text-xs text-muted-foreground">Next 30 days</p>
          </CardHeader>
          <CardContent>
            {anniversariesLoading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => (
                  <div key={i} className="flex justify-between items-center py-1.5">
                    <div className="animate-pulse bg-muted h-4 w-32 rounded"></div>
                    <div className="animate-pulse bg-muted h-4 w-16 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {anniversaries.length > 0 ? (
                  anniversaries.map((anniversary, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-1.5 px-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer"
                      onClick={handleShowAllAnniversaries}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">
                          {anniversary.firstName} {anniversary.lastName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(anniversary.anniversaryDate)} • {anniversary.yearsOfService} year{anniversary.yearsOfService !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted-foreground py-4 text-sm">
                    No upcoming anniversaries
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Birthdays */}
        <Card
          className="bg-gray-100 dark:bg-gray-800/50"
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 cursor-pointer hover:text-primary transition-colors" onClick={handleShowAllBirthdays}>
              <Gift className="h-5 w-5" />
              Upcoming Birthdays
            </CardTitle>
            <p className="text-xs text-muted-foreground">Next 30 days</p>
          </CardHeader>
          <CardContent>
            {birthdaysLoading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => (
                  <div key={i} className="flex justify-between items-center py-1.5">
                    <div className="animate-pulse bg-muted h-4 w-32 rounded"></div>
                    <div className="animate-pulse bg-muted h-4 w-16 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {birthdays.length > 0 ? (
                  birthdays.map((birthday, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-1.5 px-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer"
                      onClick={handleShowAllBirthdays}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">
                          {birthday.firstName} {birthday.lastName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(birthday.upcomingBirthday)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted-foreground py-4 text-sm">
                    No upcoming birthdays
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Departments Distribution Modal */}
      <Dialog open={showDepartmentsModal} onOpenChange={setShowDepartmentsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <Building className="h-5 w-5" />
              Departments Distribution
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {modalLoading ? (
              <div className="space-y-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex justify-between items-center py-2">
                    <div className="animate-pulse bg-muted h-4 w-40 rounded"></div>
                    <div className="animate-pulse bg-muted h-4 w-16 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {departmentBreakdown.map(({ department, count }) => (
                  <div key={department} className="flex justify-between items-center py-2 px-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-border/50">
                    <span className="font-medium text-foreground">{department}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 rounded-full bg-[#2563EB] dark:bg-[#3B82F6]" style={{ width: `${(count / Math.max(...departmentBreakdown.map(d => d.count))) * 200}px` }}></div>
                      <Badge variant="secondary" className="font-semibold employee-count-badge">
                        {count} employee{count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recent Hires Modal */}
      <Dialog open={showRecentHiresModal} onOpenChange={setShowRecentHiresModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <UserPlus className="h-5 w-5" />
              Recent Hires (Last 30 Days)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {modalLoading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <div className="animate-pulse bg-muted h-12 w-12 rounded-full"></div>
                    <div className="flex-1">
                      <div className="animate-pulse bg-muted h-4 w-32 rounded mb-1"></div>
                      <div className="animate-pulse bg-muted h-3 w-24 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentHires.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recentHires.map((employee) => (
                    <Card
                      key={employee.id}
                      className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
                      onClick={() => setSelectedEmployee(employee)}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center space-y-3">
                          <Avatar className="w-16 h-16">
                            <AvatarImage
                              src={(employee.profileImageUrl || (employee as any).photoUrl) || ''}
                              alt={`${employee.firstName} ${employee.lastName}`}
                              className="object-contain"
                            />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                              {employee.firstName[0]}{employee.lastName[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-center">
                            <h3 className="font-semibold text-base">
                              {employee.firstName} {employee.lastName}
                            </h3>
                            <Badge variant="secondary" className="mt-2">
                              {employee.department}
                            </Badge>
                            <div className="text-xs text-muted-foreground mt-1">
                              Started {new Date(employee.startDate!).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="text-center text-sm text-muted-foreground mt-4 p-2 bg-muted/10 rounded-lg">
                  Summary: {recentHires.length} new employee{recentHires.length !== 1 ? 's' : ''} hired in the last 30 days
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No new employees hired in the last 30 days
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* All Work Anniversaries Modal */}
      <Dialog open={showAllAnniversariesModal} onOpenChange={setShowAllAnniversariesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <Calendar className="h-5 w-5" />
              Work Anniversaries (Next 30 Days)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {modalLoading ? (
              <div className="space-y-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <div className="animate-pulse bg-muted h-12 w-12 rounded-full"></div>
                    <div className="flex-1">
                      <div className="animate-pulse bg-muted h-4 w-32 rounded mb-1"></div>
                      <div className="animate-pulse bg-muted h-3 w-24 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : allAnniversaries.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allAnniversaries.slice(0, 30).map((anniversary, index) => (
                    <Card
                      key={index}
                      className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
                      onClick={() => setSelectedEmployee(anniversary.employee)}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center space-y-3">
                          <Avatar className="w-16 h-16">
                            <AvatarImage
                              src={(anniversary.employee.profileImageUrl || (anniversary.employee as any).photoUrl) || ''}
                              alt={`${anniversary.firstName} ${anniversary.lastName}`}
                              className="object-contain"
                            />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                              {anniversary.firstName[0]}{anniversary.lastName[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-center">
                            <h3 className="font-semibold text-base">
                              {anniversary.firstName} {anniversary.lastName}
                            </h3>
                            <div className="text-xs text-muted-foreground mt-2">
                              Anniversary: {formatDate(anniversary.anniversaryDate)}
                            </div>
                            <Badge variant="secondary" className="mt-1">
                              {anniversary.yearsOfService} year{anniversary.yearsOfService !== 1 ? 's' : ''} of service
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No upcoming work anniversaries
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* All Birthdays Modal */}
      <Dialog open={showAllBirthdaysModal} onOpenChange={setShowAllBirthdaysModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <Gift className="h-5 w-5" />
              Upcoming Birthdays (Next 30 Days)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {modalLoading ? (
              <div className="space-y-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <div className="animate-pulse bg-muted h-12 w-12 rounded-full"></div>
                    <div className="flex-1">
                      <div className="animate-pulse bg-muted h-4 w-32 rounded mb-1"></div>
                      <div className="animate-pulse bg-muted h-3 w-24 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : allBirthdays.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allBirthdays.slice(0, 30).map((birthday, index) => (
                    <Card
                      key={index}
                      className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
                      onClick={() => setSelectedEmployee(birthday.employee)}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center space-y-3">
                          <Avatar className="w-16 h-16">
                            <AvatarImage
                              src={(birthday.employee.profileImageUrl || (birthday.employee as any).photoUrl) || ''}
                              alt={`${birthday.firstName} ${birthday.lastName}`}
                              className="object-contain"
                            />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                              {birthday.firstName[0]}{birthday.lastName[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-center">
                            <h3 className="font-semibold text-base">
                              {birthday.firstName} {birthday.lastName}
                            </h3>
                            <div className="text-xs text-muted-foreground mt-2">
                              🎂 Birthday: {formatDate(birthday.upcomingBirthday)}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No upcoming birthdays
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Country Employees Modal */}
      <Dialog open={showCountryEmployeesModal} onOpenChange={setShowCountryEmployeesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <Globe className="h-5 w-5" />
              Employees from {selectedCountry}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {modalLoading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <div className="animate-pulse bg-muted h-12 w-12 rounded-full"></div>
                    <div className="flex-1">
                      <div className="animate-pulse bg-muted h-4 w-32 rounded mb-1"></div>
                      <div className="animate-pulse bg-muted h-3 w-24 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : countryEmployees.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {countryEmployees.map((employee) => (
                    <Card
                      key={employee.id}
                      className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
                      onClick={() => setSelectedEmployee(employee)}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center space-y-3">
                          <Avatar className="w-16 h-16">
                            <AvatarImage
                              src={(employee.profileImageUrl || (employee as any).photoUrl) || ''}
                              alt={`${employee.firstName} ${employee.lastName}`}
                              className="object-contain"
                            />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                              {employee.firstName[0]}{employee.lastName[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-center">
                            <h3 className="font-semibold text-base">
                              {employee.firstName} {employee.lastName}
                            </h3>
                            <Badge variant="secondary" className="mt-2">
                              {employee.department}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="text-center text-sm text-muted-foreground mt-4 p-2 bg-muted/10 rounded-lg">
                  Summary: {countryEmployees.length} employee{countryEmployees.length !== 1 ? 's' : ''} from {selectedCountry}
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No employees found from {selectedCountry}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee Detail Modal */}
      <Dialog open={!!selectedEmployee} onOpenChange={() => setSelectedEmployee(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-foreground">
              {selectedEmployee?.firstName} {selectedEmployee?.lastName}
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="p-6 pt-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Section */}
                <div className="lg:col-span-1">
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardContent className="p-6 text-center">
                      <div className="mb-6 mt-4">
                        <Avatar className="w-40 h-40 mx-auto">
                          <AvatarImage
                            src={(selectedEmployee as any).profileImageUrl || (selectedEmployee as any).photoUrl || ''}
                            alt={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                            className="object-contain"
                          />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-4xl">
                            {selectedEmployee.firstName?.charAt(0)}{selectedEmployee.lastName?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </h3>
                      {(selectedEmployee as any).preferredName && (
                        <p className="text-muted-foreground mb-2">
                          Preferred: {(selectedEmployee as any).preferredName}
                        </p>
                      )}
                      <Badge variant="default" className="mb-4">
                        {selectedEmployee.department}
                      </Badge>
                      {(selectedEmployee as any).namePronunciation && (
                        <p className="text-sm text-muted-foreground">
                          Pronunciation: {(selectedEmployee as any).namePronunciation}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Details Section */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Contact Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Contact Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {((selectedEmployee as any).companyEmail || selectedEmployee.email) && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Company:</span>
                          <button
                            onClick={(e) => handleEmailClick((selectedEmployee as any).companyEmail || selectedEmployee.email || '', e)}
                            className="text-[#2563EB] dark:text-[#3B82F6] hover:underline text-left"
                          >
                            {(selectedEmployee as any).companyEmail || selectedEmployee.email}
                          </button>
                        </div>
                      )}
                      {(selectedEmployee as any).personalEmail && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Personal:</span>
                          <button
                            onClick={(e) => handleEmailClick((selectedEmployee as any).personalEmail || '', e)}
                            className="text-[#2563EB] dark:text-[#3B82F6] hover:underline text-left"
                          >
                            {(selectedEmployee as any).personalEmail}
                          </button>
                        </div>
                      )}
                      {(selectedEmployee as any).phoneNumber && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Phone:</span>
                          <span>{(selectedEmployee as any).phoneNumber}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Work Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5" />
                        Work Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedEmployee.startDate && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Start Date:</span>
                          <span>{new Date(selectedEmployee.startDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).timezone && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Timezone:</span>
                          <span>{(selectedEmployee as any).timezone}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Location Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Location
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedEmployee.country && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Country:</span>
                          <span>{selectedEmployee.country}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).city && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">City:</span>
                          <span>{(selectedEmployee as any).city}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).state && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">State:</span>
                          <span>{(selectedEmployee as any).state}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).address1 && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Address:</span>
                          <span>{(selectedEmployee as any).address1}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).zipCode && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Zip Code:</span>
                          <span>{(selectedEmployee as any).zipCode}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Personal Information */}
                  {(selectedEmployee as any).birthday && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="h-5 w-5" />
                          Personal Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Birthday:</span>
                          <span>{new Date((selectedEmployee as any).birthday).toLocaleDateString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Emergency Contacts */}
                  {((selectedEmployee as any).emergencyContact1Name || (selectedEmployee as any).emergencyContact2Name) && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5" />
                          Emergency Contacts
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {(selectedEmployee as any).emergencyContact1Name && (
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              Primary Contact
                            </h4>
                            <p>{(selectedEmployee as any).emergencyContact1Name}</p>
                            {(selectedEmployee as any).emergencyContact1Method && (
                              <p className="text-sm text-muted-foreground">{(selectedEmployee as any).emergencyContact1Method}</p>
                            )}
                            {(selectedEmployee as any).emergencyContact1Relationship && (
                              <p className="text-sm text-muted-foreground">Relationship: {(selectedEmployee as any).emergencyContact1Relationship}</p>
                            )}
                          </div>
                        )}
                        {(selectedEmployee as any).emergencyContact2Name && (
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              Secondary Contact
                            </h4>
                            <p>{(selectedEmployee as any).emergencyContact2Name}</p>
                            {(selectedEmployee as any).emergencyContact2Method && (
                              <p className="text-sm text-muted-foreground">{(selectedEmployee as any).emergencyContact2Method}</p>
                            )}
                            {(selectedEmployee as any).emergencyContact2Relationship && (
                              <p className="text-sm text-muted-foreground">Relationship: {(selectedEmployee as any).emergencyContact2Relationship}</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Additional Information */}
                  {((selectedEmployee as any).workVideo || (selectedEmployee as any).contactAuthorities || (selectedEmployee as any).lastUpdate) && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Briefcase className="h-5 w-5" />
                          Additional Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {(selectedEmployee as any).workVideo && (
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Work Video:</span>
                            <a
                              href={(selectedEmployee as any).workVideo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#2563EB] dark:text-[#3B82F6] hover:underline"
                            >
                              View Video
                            </a>
                          </div>
                        )}
                        {(selectedEmployee as any).contactAuthorities && (
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Contact Authorities:</span>
                            <span>{(selectedEmployee as any).contactAuthorities}</span>
                          </div>
                        )}
                        {(selectedEmployee as any).lastUpdate && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Last Updated:</span>
                            <span>{new Date((selectedEmployee as any).lastUpdate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Employee Globe Modal */}
      <EmployeeGlobe
        isOpen={showGlobeModal}
        onClose={() => setShowGlobeModal(false)}
        countryCounts={stats?.countryBreakdown?.reduce((acc, item) => {
          acc[item.country] = item.count;
          return acc;
        }, {} as Record<string, number>) || {}}
      />
    </div>
  );
};

export default EmployeeDashboard;